/**
 * AB#22171 — Product development → Live pattern versions. AB#22232 – AB#22236
 * — now the library of style codes: one row per code, Live / Not live yet /
 * Obsolete, with its production files; nothing versioned anywhere.
 *
 *   manage-style-code-files   upload, replace, delete production files; the
 *                             "checked against the TDS" tick
 *   obsolete-style-code       make a code obsolete, or bring it back (a reason)
 */
import { ref } from 'vue';

import { http, handleResponseException } from '@/interceptors/axiosRequestInterceptor';

export type CodeState = 'live' | 'not_live' | 'obsolete';

export const STATE_LABELS: Record<CodeState, string> = {
  live: 'Live',
  not_live: 'Not live yet',
  obsolete: 'Obsolete',
};

/** The counts across the top; AB#22296 adds products and the codes between them. */
export type LibraryCounts = Record<CodeState | 'missing_required' | 'products' | 'codes', number>;

/** What filters the styles: a state, or a live code missing a required file. */
export type LibraryFilter = CodeState | 'missing_required';

export const COUNT_TABS: { id: LibraryFilter; label: string }[] = [
  { id: 'live', label: 'Live' },
  { id: 'not_live', label: 'Not live yet' },
  { id: 'obsolete', label: 'Obsolete' },
  { id: 'missing_required', label: 'Missing a required file' },
];

/** AB#22296 — neck, sleeve, fabric: what sets one style of a product apart. */
export type StyleAttribute = 'neck' | 'sleeve' | 'fabric';
export const ATTRIBUTE_LABELS: Record<StyleAttribute, string> = {
  neck: 'Neck',
  sleeve: 'Sleeve',
  fabric: 'Fabric',
};

export interface CodeRow {
  id: number;
  sr?: number;
  style_code: string;
  /** AB#22296 — the garment it is a style of, the style's own name, and what sets it apart. */
  product_name: string | null;
  style_name: string | null;
  attributes: Record<StyleAttribute, string | null>;
  /** AB#22372 — who it is cut for now (the age in the code is what it was issued for). */
  age_groups?: string[];
  age_groups_label?: string | null;
  product: { id: number; name: string } | null;
  brand: string | null;
  state: CodeState;
  live_since: string | null;
  files: { count: number; missing: string[] };
  /** A live code missing a required file. */
  flagged: boolean;
  checked: { at: string; by: string | null } | null;
  where_used: { products: number; accounts: number };
  variant_of: string | null;
  development_id: number | null;
  /** AB#22312 — a replacement on its way: the one it waits for, and the products it is about to receive. */
  scheduled?: {
    out: { id: number; to: string; active_from: string } | null;
    in: { id: number; from: string; active_from: string; products: number }[];
  };
}

/** AB#22296 — a product of the library and the totals of its styles. */
export interface ProductRow {
  key: string;
  sr?: number;
  name: string | null;
  brand: string | null;
  styles: CodeRow[];
  states: Record<CodeState, number>;
  files: { count: number; missing: number };
  where_used: { products: number; accounts: number };
}

export interface ProductionFile {
  id: number;
  kind: string;
  kind_label: string;
  required: boolean;
  size: string | null;
  name: string;
  stored_name: string;
  size_bytes: number | null;
  uploaded_by: string | null;
  uploaded_at: string | null;
  development_id: number | null;
}

export interface CodeEvent {
  id: number;
  event: string;
  by: string | null;
  reason: string | null;
  data: Record<string, unknown> | null;
  at: string | null;
}

export interface CodeDetail extends CodeRow {
  /** AB#22319 AC3 — the development it came from. */
  development: { id: number; title: string; state: string } | null;
  sizes: string[];
  production_files: ProductionFile[];
  obsolete: { at: string; by: string | null; reason: string | null } | null;
  variants: string[];
  /** AB#22296 AC5 — the other styles of the same product. */
  siblings: { id: number; style_code: string; style_name: string | null; state: CodeState }[];
  /** AB#22310 — the products it serves, each since when. */
  products_on?: { id: number; name: string; brand: string | null; since: string | null }[];
  /** AB#22311 — its replacements, out of it or onto it. */
  replacements?: Replacement[];
  /** AB#22637 AC11 — the answer a version uploaded after go-live gave, which the next replacement takes. */
  pending_answer?: PendingAnswer | null;
  /** AB#22829 AC24–AC27 — the 3D model, how closely it matches, and the other codes on it. */
  model?: StyleCodeModel | null;
  events: CodeEvent[];
  /** Auto TDS — what Generate TDS prints for this style (optional: older servers send none). */
  tds_recipe?: TdsRecipe;
  /** Auto TDS — technique suggestions; free text is allowed too. */
  tds_techniques?: string[];
}

/**
 * Auto TDS — the fabrics, techniques and default comments printed on every TDS
 * generated for an order of this style. Saved through the describe PATCH as
 * `{ tds_recipe: { …only the keys that changed } }`.
 */
export interface TdsRecipe {
  /** Up to 3; a slot may be null. */
  shell_fabrics: (string | null)[];
  lining_fabrics: (string | null)[];
  logo_technique: string | null;
  name_number_technique: string | null;
  /** Up to 12 short lines, e.g. "Double shoulder". */
  comments: string[];
}

/** AB#22637 AC12 — one thing, one name: Isabelle's words for correction and improvement. */
export const KIND_LABELS: Record<'correction' | 'improvement', string> = {
  correction: 'Adjust pattern',
  improvement: 'Do not adjust pattern',
};

export interface PendingAnswer {
  kind: 'correction' | 'improvement';
  label: string;
  note: string | null;
  by: string | null;
  at: string | null;
}

/** AB#22829 AC26 — reuse, a judgement call, or a model of its own. */
export type MatchBand = 'reuse' | 'judgement' | 'own_model';
export const BAND_LABELS: Record<MatchBand, string> = {
  reuse: 'Reuse',
  judgement: 'Judgement call',
  own_model: 'Needs its own model',
};

export interface StyleCodeModel {
  name: string;
  codes: number;
  shared: boolean;
  match?: { percent: number; band: MatchBand; by: string | null; at: string | null } | null;
  siblings?: { id: number; code: string; percent: number | null; band: MatchBand | null }[];
  /** Worth a look: not a clear reuse, or shared and nobody has said how well it fits. */
  review?: boolean;
}

export type StyleDescription = Partial<
  Record<'product_name' | 'style_name' | StyleAttribute, string | null>
>;

export interface OpenOrder {
  id: number;
  order_no: string | null;
  status: string | null;
  company: string | null;
  admin: { id: number; name: string } | null;
  pinned_version?: number | null;
  url?: string;
}

export interface Impact {
  style_code: string | null;
  live_version: number | null;
  products: { id: number; name: string }[];
  open_orders: number;
  orders: OpenOrder[];
}

const result = <T>(data: unknown): T => (data as { result?: T })?.result as T;

const failure = (e: unknown, fallback: string): string => {
  const d = (e as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })
    ?.response?.data;

  return (d?.errors ? Object.values(d.errors)[0]?.[0] : undefined) || d?.message || fallback;
};

/** NR8 — what a pattern / TDS change touches. */
export const fetchImpact = async (q: {
  style_code?: string | null;
  product_id?: number | null;
}): Promise<Impact | null> => {
  const params: Record<string, string | number> = {};
  if (q.style_code) params.style_code = q.style_code;
  if (q.product_id) params.product_id = q.product_id;
  if (!Object.keys(params).length) return null;
  try {
    const { data } = await http.get('product-development/requests/impact', { params });

    return result<Impact>(data) ?? null;
  } catch {
    return null;
  }
};

export type WriteResult = { ok: boolean; message: string };

/** AB#22311 — a style code replaced by another, everywhere or on chosen products, from a date. */
export interface Replacement {
  id: number;
  from: string;
  to: string;
  direction: 'in' | 'out';
  scope: 'everywhere' | 'products';
  active_from: string;
  kind: 'correction' | 'improvement' | null;
  kind_label?: string | null;
  reason: string | null;
  state: 'scheduled' | 'done' | 'withdrawn';
  by: string | null;
  products: { id: number; name: string; brand: string | null }[];
}
export type ReplaceBody = {
  to_id: number;
  scope: 'everywhere' | 'products';
  product_ids?: number[];
  active_from: string;
  /** Left out when a version uploaded after go-live already answered it (AB#22637 AC11). */
  kind?: 'correction' | 'improvement';
  reason?: string | null;
};

/** AB#22319 AC1 — only a real code is ever sent; a placeholder such as a dash never is. */
export const isStyleCode = (v: unknown): v is string =>
  typeof v === 'string' && /^[A-Za-z0-9]{4,40}$/.test(v);

/** AB#22829 — a style code as the row: what it is, whether it carries a base TDS, who it is for, its 3D model, where it is live. */
export interface StyleCodeListRow extends CodeRow {
  category: { code: string; label: string } | null;
  base_tds: boolean;
  missing_required: string[];
  merchant: { kind: 'generic' | 'own'; company_id: number | null; name: string };
  model: StyleCodeModel | null;
  live_merchants: number;
  previous_codes: string[];
}

export interface StyleCodeFilters {
  categories: { code: string; label: string }[];
  merchants: { company_id: number; name: string }[];
  products: string[];
}

/** AB#22830 — a merchant a style code reaches, and what going live does for it. */
export interface ReachMerchant {
  company_id: number;
  name: string;
  products: number;
  published: boolean;
  pattern: 'generic' | 'own';
  served: string | null;
  no_own_code: boolean;
  status: 'publishes' | 'confirmed_generic' | 'own_code' | 'held' | 'replaced';
  disagreement: string | null;
  decided: { decision: string; by: string | null; at: string | null } | null;
  group: number;
  group_open: boolean;
}

export interface Reach {
  style_code: string;
  state: CodeState;
  guard: boolean;
  merchants: ReachMerchant[];
  counts: { total: number; generic: number; own: number; held: number };
  groups: { key: string; label: string; open: boolean; merchants: number }[];
  next_group: string | null;
}

export interface OwnPatterns {
  merchants: {
    company_id: number;
    name: string;
    source: string;
    note: string | null;
    own_codes: string[];
  }[];
  guard: { on: boolean; by: string | null; at: string | null; affected: string[] };
}

const call = async <T>(
  fn: () => Promise<{ data: unknown }>,
  fallback: string
): Promise<{ ok: boolean; message: string; result?: T }> => {
  try {
    const { data } = await fn();
    return {
      ok: true,
      message: String((data as { message?: string })?.message ?? 'Saved.'),
      result: result<T>(data) ?? undefined,
    };
  } catch (e) {
    return { ok: false, message: failure(e, fallback) };
  }
};

/** AB#22829 — the list as style codes, with its filters. */
export const fetchStyleCodeRows = async (params: Record<string, string | number>) =>
  call<{ rows: StyleCodeListRow[]; shown: number; total: number; filters: StyleCodeFilters }>(
    () => http.get('product-development/live-patterns', { params: { ...params, rows: 1 } }),
    'The style codes could not be loaded.'
  );
/** AB#22829 AC24, AC27 — how closely the live 3D model matches the pattern (null clears it). */
export const saveModelMatch = async (id: number, percent: number | null) =>
  call<{ style: CodeDetail }>(
    () => http.post(`product-development/style-codes/${id}/model-match`, { percent }),
    'The match could not be saved.'
  );
/** AB#22830 AC3–AC7, AB#22829 AC17–AC19 — where a code is live, or would go live. */
export const fetchReach = async (id: number) =>
  call<Reach>(
    () => http.get(`product-development/style-codes/${id}/reach`),
    'Could not load where it is live.'
  );
export const decideMerchant = async (
  id: number,
  company: number,
  decision: 'generic' | 'own_code'
) =>
  call<Reach>(
    () => http.post(`product-development/style-codes/${id}/merchants/${company}`, { decision }),
    'The decision could not be saved.'
  );
export const confirmRollout = async (id: number) =>
  call<Reach>(
    () => http.post(`product-development/style-codes/${id}/rollout`),
    'The next group could not be opened.'
  );
export const fetchOwnPatterns = async () =>
  call<OwnPatterns>(
    () => http.get('product-development/own-patterns'),
    'Could not load the merchants on their own pattern.'
  );
export const markOwnPattern = async (company_id: number, own: boolean) =>
  call<OwnPatterns>(
    () => http.post('product-development/own-patterns', { company_id, own }),
    'Could not save.'
  );
export const importOwnPatterns = async (list: string) =>
  call<{
    report: { marked: string[]; unmatched: string[]; ambiguous: string[] };
    merchants: OwnPatterns['merchants'];
  }>(
    () => http.post('product-development/own-patterns/import', { list }),
    'The list could not be loaded.'
  );
export const setOwnPatternGuard = async (on: boolean, reason: string | null) =>
  call<OwnPatterns>(
    () => http.put('product-development/own-patterns/guard', { on, reason }),
    'The guard could not be changed.'
  );

export function useLivePatterns() {
  const items = ref<ProductRow[]>([]);
  const total = ref(0);
  const counts = ref<LibraryCounts | null>(null);
  /** The count tab that filters the list; null: every code. */
  const tab = ref<LibraryFilter | null>(null);
  const q = ref('');
  const page = ref(1);
  const loading = ref(false);
  const allowed = ref({ files: false, obsolete: false, replace: false });
  const kinds = ref<Record<string, string>>({});
  const attributeOptions = ref<Partial<Record<StyleAttribute, Record<string, string>>>>({});
  /** Auto TDS — the technique suggestions the server sends with a code's detail. */
  const tdsTechniques = ref<string[]>([]);
  const current = ref<CodeDetail | null>(null);
  const currentLoading = ref(false);
  /** AB#22319 — what the panel is opening (its heading while it loads) and why it failed. */
  const currentHeading = ref('');
  const currentError = ref('');
  let lastOpen: (() => Promise<void>) | null = null;
  const busy = ref(false);

  const fetchList = async (over: { page?: number } = {}): Promise<void> => {
    if (over.page) page.value = over.page;
    loading.value = true;
    try {
      const params: Record<string, string | number> = { page: page.value };
      if (tab.value === 'missing_required') params.missing = 1;
      else if (tab.value) params.state = tab.value;
      if (q.value.trim()) params.q = q.value.trim();
      const { data } = await http.get('product-development/live-patterns', { params });
      const r = result<{
        products: ProductRow[];
        total: number;
        counts: LibraryCounts;
        can: { files: boolean; obsolete: boolean; replace?: boolean };
      }>(data);
      items.value = r?.products ?? [];
      total.value = Number(r?.total ?? 0);
      counts.value = r?.counts ?? null;
      allowed.value = { replace: false, ...(r?.can ?? allowed.value) };
    } catch (e) {
      handleResponseException(e);
    } finally {
      loading.value = false;
    }
  };

  const load = async (path: string, heading: string): Promise<void> => {
    lastOpen = () => load(path, heading);
    currentHeading.value = heading;
    currentError.value = '';
    current.value = null;
    currentLoading.value = true;
    try {
      const { data } = await http.get(`product-development/${path}`);
      const r = result<{
        style: CodeDetail;
        kinds: Record<string, string>;
        attributes?: Partial<Record<StyleAttribute, Record<string, string>>>;
        tds_techniques?: string[];
        can: { files: boolean; obsolete: boolean; replace?: boolean };
      }>(data);
      current.value = r?.style ?? null;
      kinds.value = r?.kinds ?? kinds.value;
      attributeOptions.value = r?.attributes ?? attributeOptions.value;
      // On the style or next to it: either is read.
      const techniques = r?.style?.tds_techniques ?? r?.tds_techniques;
      if (Array.isArray(techniques)) tdsTechniques.value = techniques;
      if (r?.can) allowed.value = { replace: false, ...r.can };
    } catch (e) {
      // AB#22319 AC8 — said inside the panel, with a way to try again; no toast.
      const status = (e as { response?: { status?: number } })?.response?.status;
      current.value = null;
      currentError.value =
        status === 404
          ? `${heading || 'This style code'} was not found. It may have been removed or renamed.`
          : `${heading || 'The style code'} could not be loaded: ${failure(e, 'the server did not answer.')}`;
    } finally {
      currentLoading.value = false;
    }
  };
  /** By the record — how a row opens (AB#22319); `heading` is shown while it loads. */
  const openById = (id: number, heading = ''): Promise<void> => load(`style-codes/${id}`, heading);
  /** By the code — a `?code=` link. Anything that is not a code is never sent. */
  const open = async (code: string): Promise<void> => {
    if (!isStyleCode(code)) {
      lastOpen = null;
      current.value = null;
      currentHeading.value = '';
      currentError.value = 'This link does not name a style code.';

      return;
    }
    await load(`live-patterns/${encodeURIComponent(code)}`, code);
  };
  const retry = (): Promise<void> => lastOpen?.() ?? Promise.resolve();
  const close = (): void => {
    current.value = null;
    currentError.value = '';
    currentHeading.value = '';
    lastOpen = null;
  };

  const write = async (
    call: () => Promise<{ data: unknown }>,
    fallback: string
  ): Promise<WriteResult> => {
    busy.value = true;
    try {
      const { data } = await call();
      const style = result<{ style?: CodeDetail }>(data)?.style;
      if (style) current.value = style;

      return { ok: true, message: (data as { message?: string })?.message ?? '' };
    } catch (e) {
      return { ok: false, message: failure(e, fallback) };
    } finally {
      busy.value = false;
    }
  };

  /** AB#22296 — the product a code is a style of, the style's name, neck, sleeve and fabric. */
  const describe = (id: number, body: StyleDescription): Promise<WriteResult> =>
    write(
      () => http.patch(`product-development/style-codes/${id}`, body),
      'The style could not be saved.'
    );

  /**
   * Auto TDS — the recipe, through the same PATCH as `describe`. Partial: only
   * the keys sent change. The response's `style` carries the saved recipe.
   */
  const setTdsRecipe = (id: number, recipe: Partial<TdsRecipe>): Promise<WriteResult> =>
    write(
      () => http.patch(`product-development/style-codes/${id}`, { tds_recipe: recipe }),
      'The TDS recipe could not be saved.'
    );

  /** AB#22233 — a production file straight to S3, then confirmed; `replacesId` replaces one. */
  const uploadFile = async (
    code: string,
    kind: string,
    file: File,
    opts: { size?: string; replacesId?: number } = {}
  ): Promise<WriteResult> => {
    busy.value = true;
    try {
      const type = file.type || 'application/octet-stream';
      const { data } = await http.post(
        `product-development/live-patterns/${encodeURIComponent(code)}/files`,
        {
          kind,
          original_name: file.name,
          content_type: type,
          size_bytes: file.size,
          ...(opts.size ? { size: opts.size } : {}),
          ...(opts.replacesId ? { replaces_id: opts.replacesId } : {}),
        }
      );
      const r = result<{ id: number; upload_url: string }>(data);
      if (!r?.upload_url) return { ok: false, message: 'The upload could not be prepared.' };
      const put = await fetch(r.upload_url, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': type },
      });
      if (!put.ok) return { ok: false, message: `The file could not be stored (${put.status}).` };
      busy.value = false;

      return await write(
        () => http.post(`product-development/style-code-files/${r.id}/confirm`),
        'Not saved.'
      );
    } catch (e) {
      return { ok: false, message: failure(e, 'The upload failed.') };
    } finally {
      busy.value = false;
    }
  };
  const deleteFile = (fileId: number) =>
    write(() => http.delete(`product-development/style-code-files/${fileId}`), 'Not deleted.');
  const downloadFile = async (fileId: number): Promise<void> => {
    try {
      const { data } = await http.get(`product-development/style-code-files/${fileId}/download`);
      const url = result<{ url: string }>(data)?.url;
      if (url) window.open(url, '_blank', 'noopener');
    } catch (e) {
      handleResponseException(e);
    }
  };
  /** AB#22232 — checked against the TDS, a person's tick. */
  const setChecked = (code: string, checked: boolean) =>
    write(
      () =>
        http.post(`product-development/live-patterns/${encodeURIComponent(code)}/check`, {
          checked,
        }),
      'Not saved.'
    );
  /** AB#22235 — obsolete, or back, with a reason. */
  /** AB#22311 — replaced by another style code (the PD lead). */
  const replaceCode = (id: number, body: ReplaceBody): Promise<WriteResult> =>
    write(
      () => http.post(`product-development/style-codes/${id}/replace`, body),
      'The replacement was not set.'
    );
  /** AB#22311 AC13 — a replacement not yet active, withdrawn. */
  const withdrawReplacement = (replacementId: number, reason: string): Promise<WriteResult> =>
    write(
      () =>
        http.post(`product-development/style-code-replacements/${replacementId}/withdraw`, {
          reason,
        }),
      'Not withdrawn.'
    );
  /** AB#22312 AC9 — a scheduled replacement moved to another date, with the reason. */
  const rescheduleReplacement = (
    replacementId: number,
    activeFrom: string,
    reason: string
  ): Promise<WriteResult> =>
    write(
      () =>
        http.post(`product-development/style-code-replacements/${replacementId}/reschedule`, {
          active_from: activeFrom,
          reason,
        }),
      'The date was not moved.'
    );
  /** The live style codes a replacement can go to. */
  const liveCodes = async (
    q: string
  ): Promise<{ id: number; style_code: string; name: string }[]> => {
    try {
      const { data } = await http.get('product-development/live-patterns', {
        params: { state: 'live', ...(q.trim() ? { q: q.trim() } : {}) },
      });
      const products = result<{ products?: ProductRow[] }>(data)?.products ?? [];

      return products.flatMap((p) =>
        p.styles.map((s) => ({
          id: s.id,
          style_code: s.style_code,
          name: [p.name, s.style_name].filter(Boolean).join(' · '),
        }))
      );
    } catch {
      return [];
    }
  };
  const makeObsolete = (code: string, reason: string) =>
    write(
      () =>
        http.post(`product-development/live-patterns/${encodeURIComponent(code)}/obsolete`, {
          reason,
        }),
      'Not saved.'
    );
  const restore = (code: string, reason: string) =>
    write(
      () =>
        http.post(`product-development/live-patterns/${encodeURIComponent(code)}/restore`, {
          reason,
        }),
      'Not saved.'
    );

  return {
    items,
    total,
    counts,
    tab,
    q,
    page,
    loading,
    allowed,
    kinds,
    current,
    currentLoading,
    currentHeading,
    currentError,
    busy,
    fetchList,
    attributeOptions,
    describe,
    tdsTechniques,
    setTdsRecipe,
    open,
    openById,
    retry,
    close,
    uploadFile,
    deleteFile,
    downloadFile,
    setChecked,
    makeObsolete,
    restore,
    replaceCode,
    withdrawReplacement,
    rescheduleReplacement,
    liveCodes,
  };
}
